import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Table,
  Badge,
  Loader,
  Alert,
  Paper,
  Group,
  Text,
  TextInput,
  Select,
  Button,
  SimpleGrid,
  Drawer,
  Stack,
  Divider,
} from '@mantine/core';
import { IconSearch, IconDownload, IconReceipt } from '@tabler/icons-react';
import api from '@/config/api';

interface PayStub {
  id: number;
  pay_date: string;
  gross_pay: string;
  net_pay: string;
  federal_tax: string;
  state_tax: string;
  status: string;
}

function fmt(val: string | number): string {
  return `$${parseFloat(String(val || '0')).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PayStubs() {
  const [stubs, setStubs] = useState<PayStub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<string>('pay_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<PayStub | null>(null);

  useEffect(() => {
    api.get('/payroll/my/pay-stubs')
      .then((res) => setStubs(res.data?.stubs || []))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load pay stubs'))
      .finally(() => setLoading(false));
  }, []);

  // Derive available years
  const years = useMemo(() => {
    const yrs = new Set(stubs.map((s) => s.pay_date?.slice(0, 4)).filter(Boolean));
    return Array.from(yrs).sort().reverse().map((y) => ({ value: y, label: y }));
  }, [stubs]);

  // Filter + sort
  const filtered = useMemo(() => {
    let result = [...stubs];
    if (yearFilter) result = result.filter((s) => s.pay_date?.startsWith(yearFilter));
    if (search) result = result.filter((s) => s.pay_date?.includes(search) || s.status?.toLowerCase().includes(search.toLowerCase()));
    result.sort((a, b) => {
      const aVal = (a as any)[sortKey] ?? '';
      const bVal = (b as any)[sortKey] ?? '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [stubs, yearFilter, search, sortKey, sortDir]);

  // YTD totals
  const ytdStubs = useMemo(() => {
    const yr = String(new Date().getFullYear());
    return stubs.filter((s) => s.pay_date?.startsWith(yr));
  }, [stubs]);
  const ytdGross = ytdStubs.reduce((s, r) => s + parseFloat(r.gross_pay || '0'), 0);
  const ytdNet = ytdStubs.reduce((s, r) => s + parseFloat(r.net_pay || '0'), 0);
  const ytdTax = ytdStubs.reduce((s, r) => s + parseFloat(r.federal_tax || '0') + parseFloat(r.state_tax || '0'), 0);

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  }, [sortKey]);

  const handleExport = useCallback(() => {
    const header = 'Pay Date,Gross Pay,Net Pay,Federal Tax,State Tax,Status\n';
    const rows = filtered.map((s) => `${s.pay_date},${s.gross_pay},${s.net_pay},${s.federal_tax},${s.state_tax},${s.status}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pay-stubs.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  if (loading) return <Loader mx="auto" mt="xl" />;
  if (error) return <Alert color="red" title="Error">{error}</Alert>;

  return (
    <Container size="lg">
      <Group justify="space-between" mb="md">
        <Title order={2}>My Pay Stubs</Title>
        <Button leftSection={<IconDownload size={16} />} variant="light" onClick={handleExport} disabled={filtered.length === 0}>
          Export CSV
        </Button>
      </Group>

      {/* YTD Summary */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
        <Paper p="md" shadow="xs" radius="md">
          <Text size="xs" c="dimmed">YTD Gross Pay</Text>
          <Text size="xl" fw={700}>{fmt(ytdGross)}</Text>
        </Paper>
        <Paper p="md" shadow="xs" radius="md">
          <Text size="xs" c="dimmed">YTD Net Pay</Text>
          <Text size="xl" fw={700} c="green">{fmt(ytdNet)}</Text>
        </Paper>
        <Paper p="md" shadow="xs" radius="md">
          <Text size="xs" c="dimmed">YTD Taxes</Text>
          <Text size="xl" fw={700} c="red">{fmt(ytdTax)}</Text>
        </Paper>
      </SimpleGrid>

      {/* Filters */}
      <Group mb="md" gap="sm">
        <TextInput
          leftSection={<IconSearch size={16} />}
          placeholder="Search by date..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 300 }}
        />
        <Select
          placeholder="All Years"
          clearable
          value={yearFilter}
          onChange={setYearFilter}
          data={years}
          w={120}
        />
      </Group>

      {filtered.length === 0 ? (
        <Alert color="blue" icon={<IconReceipt size={16} />}>No pay stubs match your filters.</Alert>
      ) : (
        <Paper shadow="xs" radius="md" style={{ overflow: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                {[
                  { key: 'pay_date', label: 'Pay Date' },
                  { key: 'gross_pay', label: 'Gross Pay' },
                  { key: 'net_pay', label: 'Net Pay' },
                  { key: 'federal_tax', label: 'Federal Tax' },
                  { key: 'state_tax', label: 'State Tax' },
                  { key: 'status', label: 'Status' },
                ].map((col) => (
                  <Table.Th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    {col.label} {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((stub) => (
                <Table.Tr
                  key={stub.id}
                  onClick={() => setSelected(stub)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>{stub.pay_date}</Table.Td>
                  <Table.Td>{fmt(stub.gross_pay)}</Table.Td>
                  <Table.Td fw={600} c="green">{fmt(stub.net_pay)}</Table.Td>
                  <Table.Td>{fmt(stub.federal_tax)}</Table.Td>
                  <Table.Td>{fmt(stub.state_tax)}</Table.Td>
                  <Table.Td><Badge color={stub.status === 'paid' ? 'green' : 'orange'}>{stub.status}</Badge></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Detail Drawer */}
      <Drawer
        opened={!!selected}
        onClose={() => setSelected(null)}
        title="Pay Stub Details"
        position="right"
        size="md"
      >
        {selected && (
          <Stack gap="md">
            <Divider label="Pay Period" labelPosition="left" />
            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Pay Date</Text>
                <Text fw={500}>{selected.pay_date}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Status</Text>
                <Badge color={selected.status === 'paid' ? 'green' : 'orange'}>{selected.status}</Badge>
              </div>
            </SimpleGrid>

            <Divider label="Earnings" labelPosition="left" />
            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Gross Pay</Text>
                <Text size="lg" fw={700}>{fmt(selected.gross_pay)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Net Pay</Text>
                <Text size="lg" fw={700} c="green">{fmt(selected.net_pay)}</Text>
              </div>
            </SimpleGrid>

            <Divider label="Taxes" labelPosition="left" />
            <SimpleGrid cols={2}>
              <div>
                <Text size="xs" c="dimmed">Federal Tax</Text>
                <Text fw={500}>{fmt(selected.federal_tax)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">State Tax</Text>
                <Text fw={500}>{fmt(selected.state_tax)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">Total Tax</Text>
                <Text fw={500} c="red">
                  {fmt(parseFloat(selected.federal_tax || '0') + parseFloat(selected.state_tax || '0'))}
                </Text>
              </div>
            </SimpleGrid>
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}
